import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import type { FirebaseStorage } from 'firebase/storage';
import type {
  AppStats,
  Currency,
  PromotionPlanId,
  PromotionStatus,
  StatsRange,
} from '@talkofthetown/shared';
import { config } from './config';

export const app = initializeApp(config.firebase);

// App Check must start before other services send requests, or enforced endpoints reject them.
if (!config.useEmulators && config.recaptchaSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(config.recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

if (config.useEmulators) {
  const host = config.emulatorHost;
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8180);
  connectFunctionsEmulator(functions, host, 5001);
}

let storagePromise: Promise<FirebaseStorage> | null = null;

/** Storage is only needed when posting, so it's loaded on demand to keep the first page light. */
export function getStorageLazy(): Promise<FirebaseStorage> {
  storagePromise ??= import('firebase/storage').then(({ getStorage, connectStorageEmulator }) => {
    const storage = getStorage(app);
    if (config.useEmulators) connectStorageEmulator(storage, config.emulatorHost, 9199);
    return storage;
  });
  return storagePromise;
}

export const callables = {
  createPost: httpsCallable<unknown, { id: string }>(functions, 'createPost'),
  reportPost: httpsCallable<{ postId: string; reason: string }, { hidden: boolean }>(
    functions,
    'reportPost',
  ),
  claimAdmin: httpsCallable<void, { admin: boolean }>(functions, 'claimAdmin'),
  adminStats: httpsCallable<{ range: StatsRange }, AppStats>(functions, 'adminStats'),
  deletePost: httpsCallable<{ postId: string }, { ok: true }>(functions, 'deletePost'),
  startPromotion: httpsCallable<
    { postId: string; plan: PromotionPlanId; currency: Currency },
    {
      orderId: string;
      keyId: string;
      amount: number;
      currency: Currency;
      description: string;
      name: string | null;
      email: string | null;
    }
  >(functions, 'startPromotion'),
  confirmPromotion: httpsCallable<
    { orderId: string; paymentId: string; signature: string },
    { status: PromotionStatus }
  >(functions, 'confirmPromotion'),
  reviewPromotion: httpsCallable<
    { orderId: string; approve: boolean },
    { status: PromotionStatus }
  >(functions, 'reviewPromotion'),
  adminAction: httpsCallable<
    { action: string; postId?: string; uid?: string },
    { ok: true; affected?: number }
  >(functions, 'adminAction'),
};
